/**
 * Ver e editar um registro em PAGINA INTEIRA, nao em painel lateral.
 *
 * O painel lateral obrigava tudo a caber numa coluna estreita: catorze campos de relatorio viravam
 * uma rolagem longa, a URL de embed saia cortada, e o botao Salvar ficava no fim de um scroll que
 * ninguem termina. Em sistema de administracao, editar e uma TELA - com os grupos lado a lado, a
 * identificacao no topo e as acoes sempre alcancaveis.
 *
 * A casca (sidebar e barra superior) continua: a pessoa nao sai do sistema, troca de contexto
 * dentro dele. O registro assume o cabecalho da pagina, com o caminho de volta explicito no topo e
 * o nome da colecao nele.
 */

import { Button } from '@ledger-glass/react'
import { useId, useState, type ReactNode } from 'react'

import { Alert } from '../components/Alert'
import { PageHeader } from '../shell/AppShell'
import { GRUPO_PADRAO, naoCatalogados, porGrupo, type CampoMeta } from './campos'

/** "clientes" -> "Clientes". `capitalize` do CSS faria "Políticas De RLS". */
export function maiuscula(texto: string): string {
  return texto.charAt(0).toLocaleUpperCase() + texto.slice(1)
}

/** Copia sem sair da tela. GUID existe para ir para outro lugar - selecionar a mao erra. */
export function BotaoCopiar({ valor }: { valor: string }) {
  const [copiado, setCopiado] = useState(false)
  return (
    <button
      className="copiar"
      onClick={() => {
        void navigator.clipboard
          ?.writeText(valor)
          .then(() => {
            setCopiado(true)
            window.setTimeout(() => setCopiado(false), 1500)
          })
          .catch(() => undefined)
      }}
      type="button"
    >
      {copiado ? 'copiado' : 'copiar'}
    </button>
  )
}

export function Cabecalho({
  colecao,
  sobretitulo,
  titulo,
  id,
  aoVoltar,
  acoes,
}: {
  colecao: string
  sobretitulo: string
  titulo: string
  id: string
  aoVoltar: () => void
  acoes?: ReactNode
}) {
  return (
    <PageHeader
      acoes={acoes}
      descricao={
        <span className="registro__id">
          <code>{id}</code>
          <BotaoCopiar valor={id} />
        </span>
      }
      sobretitulo={maiuscula(sobretitulo)}
      titulo={titulo}
      voltar={
        <Button icon="chevronLeft" onClick={aoVoltar} size="small" variant="ghost">
          {maiuscula(colecao)}
        </Button>
      }
    />
  )
}

function valorFormatado<T>(item: T, campo: CampoMeta<T>): ReactNode {
  if (campo.exibir !== undefined) return campo.exibir(item)
  const bruto = (item as Record<string, unknown>)[campo.nome]
  if (bruto === null || bruto === undefined || bruto === '') return <span className="vazio">—</span>
  if (typeof bruto === 'boolean') return bruto ? 'sim' : 'não'
  const texto = String(bruto)
  if (campo.formato === 'identificador') {
    return (
      <span className="valor-id">
        <code>{texto}</code>
        <BotaoCopiar valor={texto} />
      </span>
    )
  }
  if (campo.formato === 'codigo') return <code>{texto}</code>
  if (campo.formato === 'url') {
    return (
      <a className="valor-url" href={texto} rel="noreferrer" target="_blank">
        {texto}
      </a>
    )
  }
  return texto
}

export function PaginaDeDetalhes<T>({
  item,
  campos,
  colecao,
  rotulo,
  titulo,
  id,
  aoVoltar,
  aoEditar,
}: {
  item: T
  campos: Array<CampoMeta<T>>
  colecao: string
  rotulo: string
  titulo: string
  id: string
  aoVoltar: () => void
  aoEditar?: () => void
}) {
  const visiveis = campos.filter((campo) => campo.oculto !== true)
  const extras = naoCatalogados(item, campos)

  return (
    <article className="registro">
      <Cabecalho
        acoes={
          aoEditar === undefined ? undefined : (
            <Button onClick={aoEditar}>Editar</Button>
          )
        }
        aoVoltar={aoVoltar}
        colecao={colecao}
        id={id}
        sobretitulo={rotulo}
        titulo={titulo}
      />

      <div className="registro__grupos">
        {porGrupo(visiveis).map(([grupo, doGrupo]) => (
          <section className="painel" key={grupo}>
            <h3 className="painel__titulo">{grupo}</h3>
            <dl className="detalhes">
              {doGrupo.map((campo) => (
                <div key={campo.nome}>
                  <dt>{campo.rotulo}</dt>
                  <dd>{valorFormatado(item, campo)}</dd>
                  {campo.nota === undefined ? null : (
                    <p className="detalhes__nota">{campo.nota}</p>
                  )}
                </div>
              ))}
            </dl>
          </section>
        ))}

        {extras.length === 0 ? null : (
          <section className="painel">
            <h3 className="painel__titulo">Outros dados</h3>
            <dl className="detalhes">
              {extras.map((chave) => {
                const bruto = (item as Record<string, unknown>)[chave]
                return (
                  <div key={chave}>
                    <dt>{chave}</dt>
                    <dd>
                      {bruto === null || bruto === undefined || bruto === '' ? (
                        <span className="vazio">—</span>
                      ) : typeof bruto === 'object' ? (
                        <code>{JSON.stringify(bruto)}</code>
                      ) : (
                        String(bruto)
                      )}
                    </dd>
                  </div>
                )
              })}
            </dl>
          </section>
        )}
      </div>
    </article>
  )
}

export function PaginaDeEdicao<T>({
  item,
  campos,
  colecao,
  rotulo,
  titulo,
  id,
  aviso,
  erro,
  salvando,
  aoVoltar,
  aoSalvar,
}: {
  item: T
  campos: Array<CampoMeta<T>>
  colecao: string
  rotulo: string
  titulo: string
  id: string
  aviso?: ReactNode
  erro?: ReactNode
  salvando: boolean
  aoVoltar: () => void
  aoSalvar: (mudancas: Record<string, unknown>) => void
}) {
  const campoId = useId()
  const editaveis = campos.filter((campo) => campo.edicao !== undefined)
  const original = item as Record<string, unknown>

  const [rascunho, setRascunho] = useState<Record<string, string | boolean>>(() => {
    const inicial: Record<string, string | boolean> = {}
    for (const campo of editaveis) {
      const valor = original[campo.nome]
      inicial[campo.nome] =
        campo.edicao?.tipo === 'booleano'
          ? valor === true
          : valor === null || valor === undefined
            ? ''
            : String(valor)
    }
    return inicial
  })

  /** So o que mudou: o backend recusa trocas que a pessoa nem fez, se o valor for reenviado. */
  function mudancas(): Record<string, unknown> {
    const saida: Record<string, unknown> = {}
    for (const campo of editaveis) {
      const atual = rascunho[campo.nome]
      const antes = original[campo.nome]
      const valor =
        campo.edicao?.tipo === 'booleano'
          ? atual === true
          : campo.edicao?.opcional === true && atual === ''
            ? null
            : String(atual ?? '')
      const iguais = valor === antes || (valor === null && (antes === null || antes === undefined))
      if (!iguais) saida[campo.nome] = valor
    }
    return saida
  }

  const alteracoes = mudancas()
  const quantas = Object.keys(alteracoes).length

  return (
    <form
      className="registro"
      noValidate
      onSubmit={(evento) => {
        evento.preventDefault()
        if (quantas > 0) aoSalvar(alteracoes)
      }}
    >
      <Cabecalho
        acoes={
          <div className="barra-acoes">
            <span className="barra-acoes__estado">
              {quantas === 0
                ? 'Nada alterado'
                : `${quantas} campo${quantas > 1 ? 's' : ''} alterado${quantas > 1 ? 's' : ''}`}
            </span>
            <Button onClick={aoVoltar} type="button" variant="ghost">
              Cancelar
            </Button>
            <Button disabled={quantas === 0 || salvando} type="submit">
              {salvando ? 'Salvando…' : 'Salvar alterações'}
            </Button>
          </div>
        }
        aoVoltar={aoVoltar}
        colecao={colecao}
        id={id}
        sobretitulo={`Editar ${rotulo}`}
        titulo={titulo}
      />

      <div className="registro__grupos">
        {porGrupo(editaveis).map(([grupo, doGrupo]) => (
          <section className="painel" key={grupo}>
            <h3 className="painel__titulo">{grupo === GRUPO_PADRAO ? rotulo : grupo}</h3>
            <div className="campos-grid">
              {doGrupo.map((campo) => {
                const idDoCampo = `${campoId}-${campo.nome}`
                const valor = rascunho[campo.nome]
                const edicao = campo.edicao
                if (edicao === undefined) return null
                const alterado = Object.hasOwn(alteracoes, campo.nome)
                const largo = edicao.tipo === 'textarea' || campo.formato === 'url'

                if (edicao.tipo === 'booleano') {
                  return (
                    <label
                      className={`campo-booleano${alterado ? ' campo--alterado' : ''}`}
                      htmlFor={idDoCampo}
                      key={campo.nome}
                    >
                      <input
                        checked={valor === true}
                        id={idDoCampo}
                        onChange={(evento) =>
                          setRascunho((atual) => ({ ...atual, [campo.nome]: evento.target.checked }))
                        }
                        type="checkbox"
                      />
                      <span>{campo.rotulo}</span>
                    </label>
                  )
                }

                return (
                  <div
                    className={`campo${largo ? ' campo--largo' : ''}${alterado ? ' campo--alterado' : ''}`}
                    key={campo.nome}
                  >
                    <label htmlFor={idDoCampo}>{campo.rotulo}</label>
                    {edicao.tipo === 'selecao' ? (
                      <select
                        id={idDoCampo}
                        onChange={(evento) =>
                          setRascunho((atual) => ({ ...atual, [campo.nome]: evento.target.value }))
                        }
                        value={String(valor ?? '')}
                      >
                        {edicao.opcional === true ? <option value="">— nenhum —</option> : null}
                        {(edicao.opcoes?.(item) ?? []).map((opcao) => (
                          <option key={opcao.valor} value={opcao.valor}>
                            {opcao.rotulo}
                          </option>
                        ))}
                      </select>
                    ) : edicao.tipo === 'textarea' ? (
                      <textarea
                        id={idDoCampo}
                        onChange={(evento) =>
                          setRascunho((atual) => ({ ...atual, [campo.nome]: evento.target.value }))
                        }
                        rows={3}
                        value={String(valor ?? '')}
                      />
                    ) : (
                      <input
                        id={idDoCampo}
                        onChange={(evento) =>
                          setRascunho((atual) => ({ ...atual, [campo.nome]: evento.target.value }))
                        }
                        placeholder={edicao.exemplo}
                        value={String(valor ?? '')}
                      />
                    )}
                    {edicao.dica === undefined ? null : (
                      <p className="campo__dica">{edicao.dica}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      {aviso === undefined ? null : <Alert tone="info">{aviso}</Alert>}
      {erro === undefined ? null : <Alert tone="danger">{erro}</Alert>}
    </form>
  )
}
